import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import {
  ChartSessionApi,
  ChartSessionItem,
  ChartSessionItemApi,
  ChartSessionItemSchema,
  ChartSessionMessage,
  ChartSessionMessagesApi,
  chartSessionMessagesSchema,
  chartSessionSchema,
  ChartSessionGlobalContext,
} from '@/entities/ChartSession';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { CollectionStore } from '@/stores/local/shared/CollectionStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import call from '@/utils/api';
import { schemeParse } from '@/utils/schemeParse';

const PAGE_SIZE = 20;

type FetchSessionsOptions = {
  withMeta?: boolean;
  reset?: boolean;
};

type ChartSessionsStoreParams = {
  organizationId: number;
};

type PrivateFields = '_organizationId' | '_activeSession' | '_page' | '_total' | '_pageSize';

export class ChartSessionsStore implements ILocalStore {
  readonly initMeta = new MetaStore();
  readonly meta = new MetaStore();
  readonly nextPageMeta = new MetaStore();
  readonly loadSessionMeta = new MetaStore();
  readonly createSessionMeta = new MetaStore();
  readonly getSessionMessagesMeta = new MetaStore();
  readonly getSessionByIdMeta = new MetaStore();
  readonly updateSessionTitleMeta = new MetaStore();

  readonly collection = new CollectionStore<ChartSessionItem, ChartSessionItem>({
    parseItem: (data: ChartSessionItem) => data,
    getEntityKey: (raw) => raw.id,
  });

  readonly sessionMessagesCollection = new CollectionStore<ChartSessionMessage, ChartSessionMessage>({
    parseItem: (data: ChartSessionMessage) => data,
    getEntityKey: (raw) => raw.id,
  });

  private readonly _organizationId: number;
  private _activeSession: ChartSessionItem | null = null;
  private _page = 1;
  private _total = 0;
  private _pageSize = PAGE_SIZE;

  constructor({ organizationId }: ChartSessionsStoreParams) {
    this._organizationId = organizationId;

    makeObservable<ChartSessionsStore, PrivateFields>(this, {
      _organizationId: observable,
      _activeSession: observable.ref,
      _page: observable,
      _total: observable,
      _pageSize: observable,
      fetchSessions: action.bound,
      fetchMoreSessions: action.bound,
      createSession: action.bound,
      getSessionMessages: action.bound,
      getSessionById: action.bound,
      updateSessionTitle: action.bound,
      resetSessions: action.bound,
      activeSessionId: computed,
      activeSession: computed,
      hasMoreSessions: computed,
      sessionMessages: computed,
      sessionsTotal: computed,
    });
  }

  get activeSessionId(): string | null {
    return this._activeSession?.id ?? null;
  }

  get activeSession(): ChartSessionItem | null {
    return this._activeSession;
  }

  get hasMoreSessions(): boolean {
    return this.collection.size < this._total;
  }

  get sessionMessages(): ChartSessionMessage[] {
    return this.sessionMessagesCollection.items;
  }

  get sessionsTotal(): number {
    return this._total;
  }

  async fetchSessions(options: FetchSessionsOptions = {}): Promise<void> {
    const { reset = false, withMeta = true } = options;

    if (reset) {
      this._page = 1;
    }

    if (withMeta) {
      this.meta.setLoadedStartMeta();
    }

    const shouldReplace = reset || this._page <= 1;

    const response = await call<ChartSessionApi>({
      endpoint: ENDPOINTS.chartSessions.get(String(this._organizationId)),
      config: {
        params: {
          page: this._page,
          page_size: this._pageSize,
        },
      },
    });

    if (response.isError) {
      if (withMeta) {
        this.meta.setLoadedErrorMeta();
      }

      return;
    }

    const parsed = schemeParse(chartSessionSchema, response.data);

    if (!parsed.success) {
      if (withMeta) {
        this.meta.setLoadedErrorMeta();
      }

      return;
    }

    runInAction(() => {
      if (shouldReplace) {
        this.collection.fromArray(parsed.data.items);
      } else {
        this.collection.pushRaws(parsed.data.items);
      }

      this._total = response.paginator?.total_count ?? parsed.data.total ?? this.collection.size;
      this._pageSize = response.paginator?.page_size ?? this._pageSize;
      this._page = response.paginator?.page ?? this._page;

      if (withMeta) {
        this.meta.setLoadedSuccessMeta();
      }
    });
  }

  async fetchMoreSessions(): Promise<void> {
    if (!this.hasMoreSessions || this.nextPageMeta.isLoading) {
      return;
    }

    this.nextPageMeta.setLoadedStartMeta();
    this._page += 1;

    const response = await call<ChartSessionApi>({
      endpoint: ENDPOINTS.chartSessions.get(String(this._organizationId)),
      config: {
        params: {
          page: this._page,
          page_size: this._pageSize,
        },
      },
    });

    if (response.isError) {
      this._page -= 1;
      this.nextPageMeta.setLoadedErrorMeta();

      return;
    }

    const parsed = schemeParse(chartSessionSchema, response.data);

    if (!parsed.success) {
      this._page -= 1;
      this.nextPageMeta.setLoadedErrorMeta();

      return;
    }

    runInAction(() => {
      this.collection.pushRaws(parsed.data.items);
      this._total = response.paginator?.total_count ?? parsed.data.total ?? this.collection.size;
      this._pageSize = response.paginator?.page_size ?? this._pageSize;
      this._page = response.paginator?.page ?? this._page;
      this.nextPageMeta.setLoadedSuccessMeta();
    });
  }

  async getSessionMessages(sessionId: string): Promise<void> {
    this.sessionMessagesCollection.clear();
    this.getSessionMessagesMeta.setLoadedStartMeta();

    const response = await call<ChartSessionMessagesApi>({
      endpoint: ENDPOINTS.chartSessions.getSessionMessages(String(this._organizationId), sessionId),
    });

    if (response.isError) {
      this.getSessionMessagesMeta.setLoadedErrorMeta();

      return;
    }

    runInAction(() => {
      const parsed = schemeParse(chartSessionMessagesSchema, response.data);

      if (!parsed.success) {
        this.getSessionMessagesMeta.setLoadedErrorMeta();

        return;
      }

      this.sessionMessagesCollection.fromArray(parsed.data.items);
      this.getSessionMessagesMeta.setLoadedSuccessMeta();
    });
  }

  async getSessionById(sessionId: string): Promise<void> {
    this.getSessionByIdMeta.setLoadedStartMeta();

    const response = await call<ChartSessionItemApi>({
      endpoint: ENDPOINTS.chartSessions.getSessionById(String(this._organizationId), sessionId),
    });

    runInAction(() => {
      if (response.isError) {
        this.getSessionByIdMeta.setLoadedErrorMeta();

        return;
      }

      const parsed = schemeParse(ChartSessionItemSchema, response.data);

      if (!parsed.success) {
        this.getSessionByIdMeta.setLoadedErrorMeta();

        return;
      }

      this._activeSession = parsed.data;
      this.getSessionByIdMeta.setLoadedSuccessMeta();
    });
  }

  async updateSessionTitle(sessionId: string, title: string): Promise<boolean> {
    this.updateSessionTitleMeta.setLoadedStartMeta();

    const response = await call<ChartSessionItemApi>({
      endpoint: ENDPOINTS.chartSessions.updateSession(String(this._organizationId), sessionId),
      method: 'PATCH',
      data: { title },
    });

    if (response.isError) {
      this.updateSessionTitleMeta.setLoadedErrorMeta();

      return false;
    }

    const parsed = schemeParse(ChartSessionItemSchema, response.data);

    if (!parsed.success) {
      this.updateSessionTitleMeta.setLoadedErrorMeta();

      return false;
    }

    const updated = parsed.data;

    runInAction(() => {
      if (this._activeSession?.id === sessionId) {
        this._activeSession = updated;
      }

      this.collection.updateItemByKey(sessionId, { title: updated.title ?? null });
      this.updateSessionTitleMeta.setLoadedSuccessMeta();
    });

    return true;
  }

  async createSession(globalContext: ChartSessionGlobalContext): Promise<void> {
    this.createSessionMeta.setLoadedStartMeta();

    const response = await call<ChartSessionItemApi>({
      endpoint: ENDPOINTS.chartSessions.create(String(this._organizationId)),
      method: 'POST',
      data: {
        global_context: globalContext,
      },
    });

    if (response.isError) {
      this.createSessionMeta.setLoadedErrorMeta();
      this.loadSessionMeta.setLoadedErrorMeta();

      return;
    }

    const parsed = schemeParse(ChartSessionItemSchema, response.data);

    if (!parsed.success) {
      this.createSessionMeta.setLoadedErrorMeta();
      this.loadSessionMeta.setLoadedErrorMeta();

      return;
    }

    this._activeSession = parsed.data;
    this.createSessionMeta.setLoadedSuccessMeta();
    this.loadSessionMeta.setLoadedSuccessMeta();
  }

  resetSessions(): void {
    this.collection.clear();
    this.sessionMessagesCollection.clear();
    this._page = 1;
    this._total = 0;
    this._pageSize = PAGE_SIZE;
    this._activeSession = null;
    this.meta.reset();
    this.nextPageMeta.reset();
  }

  destroy(): void {
    this.initMeta.destroy();
    this.meta.destroy();
    this.nextPageMeta.destroy();
    this.loadSessionMeta.destroy();
    this.createSessionMeta.destroy();
    this.getSessionMessagesMeta.destroy();
    this.getSessionByIdMeta.destroy();
    this.updateSessionTitleMeta.destroy();
    this.collection.destroy();
    this.sessionMessagesCollection.destroy();
  }
}
